import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Key, User, ArrowRight, CheckCircle, Loader2, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { apiRegister, apiVerify, apiLogin, setUser } from "@/lib/storage";
import { toast } from "sonner";

const UCL_LOGO = "https://customer-assets.emergentagent.com/job_sports-predict-hub-2/artifacts/5td1jvbh_image.png";
const KCL_LOGO = "https://customer-assets.emergentagent.com/job_sports-predict-hub-2/artifacts/8qd91t00_image.png";

export default function SignUp() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("signup"); // "signup" or "login"
  const [step, setStep] = useState(1); // 1: email, 2: verify+username
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sentCode, setSentCode] = useState("");

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setLoading(true);
    try {
      // Login now requires the same verification-code step as signup —
      // there is no more code-less login.
      const data = mode === "signup" ? await apiRegister(email) : await apiLogin(email);
      if (data.university) setUniversity(data.university);
      setSentCode(data.code_hint || "");
      toast.success(`Verification code sent to ${email}`);
      setStep(2);
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    if (mode === "signup" && username.trim().length < 2) { toast.error("Username must be at least 2 characters"); return; }
    setLoading(true);
    try {
      const data = await apiVerify(email, code, mode === "signup" ? username.trim() : undefined);
      setUser(data.user);
      window.dispatchEvent(new Event("auth-updated"));
      toast.success(mode === "signup"
        ? `Welcome, ${data.user.username}! Start picking winners to earn points.`
        : `Welcome back, ${data.user.username}!`);
      navigate("/");
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex" data-testid="signup-page">
      {/* Left decorative panel */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden hero-gradient">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 via-transparent to-purple-600/5" />
        <div className="relative z-10 text-center p-12">
          <h1 className="text-5xl font-bold text-white mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            PREDICT<span className="text-red-500">IT</span>
          </h1>
          <p className="text-slate-400 text-sm mb-8 max-w-sm mx-auto">
            The ultimate varsity fantasy prediction game. Pick winners. Earn points. Represent your university.
          </p>
          <div className="flex items-center justify-center gap-6">
            <img src={UCL_LOGO} alt="UCL" className="h-16 object-contain" />
            <span className="text-2xl text-slate-600 font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>VS</span>
            <img src={KCL_LOGO} alt="KCL" className="h-16 object-contain" />
          </div>
          <p className="text-[10px] text-slate-600 mt-6 uppercase tracking-widest">45 fixtures across 8 days</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 lg:max-w-lg flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center">
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              PREDICT<span className="text-red-500">IT</span>
            </h1>
            <p className="text-xs text-slate-500 mt-1">Varsity Fantasy Predictions</p>
          </div>

          {/* Mode toggle */}
          <div className="flex mb-6 bg-white/5 rounded-sm p-0.5" data-testid="auth-mode-toggle">
            <button
              onClick={() => { setMode("signup"); setStep(1); }}
              data-testid="mode-signup"
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${mode === "signup" ? "bg-red-600 text-white" : "text-slate-500 hover:text-white"}`}
            >Sign Up</button>
            <button
              onClick={() => { setMode("login"); setStep(1); }}
              data-testid="mode-login"
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${mode === "login" ? "bg-red-600 text-white" : "text-slate-500 hover:text-white"}`}
            >Log In</button>
          </div>

          {step === 1 ? (
            <div className="anim-fade-in-up">
              <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {mode === "signup" ? "SIGN UP" : "WELCOME BACK"}
              </h2>
              <p className="text-xs text-slate-500 mb-6">
                {mode === "signup" ? "Enter your university email to get started." : "Log in with your registered university email."}
              </p>
              <form onSubmit={handleSendCode} className="space-y-4">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-widest block mb-1.5">University Email</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      data-testid={mode === "signup" ? "signup-email" : "login-email"}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@ucl.ac.uk"
                      className="pl-9 bg-white/5 border-white/10 h-11 focus:border-red-500"
                      autoFocus
                    />
                  </div>
                  {mode === "signup" && (
                    <p className="text-[10px] text-slate-600 mt-1.5">Supports UCL, KCL, Imperial, LSE, and other .ac.uk/.edu domains</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid={mode === "signup" ? "signup-send-code" : "login-submit"}
                  className="w-full py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-widest rounded-sm transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : mode === "signup" ? <ArrowRight size={14} /> : <LogIn size={14} />}
                  Send Verification Code
                </button>
              </form>
            </div>
          ) : (
            <div className="anim-fade-in-up">
              <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {mode === "signup" ? "VERIFY & JOIN" : "VERIFY TO LOG IN"}
              </h2>
              <p className="text-xs text-slate-500 mb-2">Code sent to <span className="text-white">{email}</span></p>

              {university && (
                <div className="mb-4 p-3 bg-white/5 border border-white/10 rounded-sm flex items-center gap-3">
                  <CheckCircle size={14} className="text-green-400" />
                  <div>
                    <p className="text-xs font-semibold text-white">{university.full}</p>
                    <p className="text-[10px] text-slate-500">You'll join the {university.name} leaderboard</p>
                  </div>
                </div>
              )}

              {sentCode && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
                  <p className="text-[10px] text-red-400 uppercase tracking-widest mb-1">Dev Mode — Your Code</p>
                  <p className="text-2xl font-bold font-mono text-red-400 tracking-[0.3em]" data-testid="demo-code">{sentCode}</p>
                </div>
              )}

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-widest block mb-1.5">Verification Code</label>
                  <div className="relative">
                    <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      data-testid="signup-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      className="pl-9 bg-white/5 border-white/10 h-11 text-lg font-mono tracking-[0.3em] text-center focus:border-red-500"
                      maxLength={6}
                    />
                  </div>
                </div>
                {mode === "signup" && (
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-widest block mb-1.5">Choose Username</label>
                    <div className="relative">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                      <Input
                        data-testid="signup-username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="YourPredictorName"
                        className="pl-9 bg-white/5 border-white/10 h-11 focus:border-red-500"
                        maxLength={20}
                      />
                    </div>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="signup-verify"
                  className="w-full py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-widest rounded-sm transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  {mode === "signup" ? "Verify & Start Predicting" : "Verify & Log In"}
                </button>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-center text-[10px] text-slate-500 hover:text-slate-400 uppercase tracking-widest"
                >
                  Back to email
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
